import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CopyrightStatementModal.css';

interface CopyrightStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CopyrightStatementModal: React.FC<CopyrightStatementModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const modalContent = (
    <div
      className="copyright-modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div className="copyright-modal-container">
        <div className="copyright-modal-header">
          <h2>Copyright and Attribution</h2>
          <button
            className="copyright-modal-close"
            onClick={handleCloseClick}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="copyright-modal-content">
          <div className="copyright-content">
            <p>© 2026 Gymea Edu. All rights reserved except as permitted under the Terms of Use.</p>

            <p>
              Except where otherwise stated, all software, design, templates, and original content on this website are the intellectual property of Gymea Edu.
            </p>

            <p>This platform incorporates and references curriculum materials from the following authorities:</p>

            <h3>Victorian Curriculum (VCAA)</h3>
            <p>
              The Victorian Curriculum F-10 content elements are © VCAA, reproduced by permission. Some curriculum elements in this resource may be modified extracts. The VCAA does not endorse or make any warranties regarding this resource and accepts no responsibility for incomplete or inaccurate information presented in this resource. The latest Victorian Curriculum F-10 and related content can be accessed directly at{' '}
              <a
                href="https://f10.vcaa.vic.edu.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="copyright-link"
              >
                the VCAA website
              </a>
              .
            </p>

            <h3>Australian Curriculum (ACARA)</h3>
            <p>
              The Australian Curriculum and National Literacy and Numeracy Learning Progressions are © ACARA and licensed under the Creative Commons Attribution 4.0 International (CC BY 4.0) licence. Use of these materials does not imply endorsement by ACARA.
            </p>

            <h3>NSW Curriculum (NESA)</h3>
            <p>
              NSW Curriculum and Syllabus materials are © NSW Education Standards Authority (NESA) for and on behalf of the Crown in right of the State of New South Wales.
            </p>

            <p>All curriculum materials are used for professional, educational and non-commercial purposes only.</p>
            <p>Gymea Edu is not endorsed by VCAA, ACARA, or NESA.</p>
            <p>Gymea Edu makes no warranties as to the accuracy, completeness, or currency of any curriculum-related content displayed on this platform.</p>
            <p>Users must refer to the official websites of the relevant authorities for authoritative and current versions of curriculum documents.</p>
          </div>
        </div>

        <div className="copyright-modal-footer">
          <button className="copyright-modal-close-button" onClick={handleCloseClick} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render the modal at the document body level
  return createPortal(modalContent, document.body);
};